import {
  KitWithResultsSchemaType,
  KitOrderResultLineWithCorrectionSchemaType,
  KitOrderResultLineSchemaType,
  KitOrderLabInfoSchemaType,
} from '@/types/portal-api.generated'

const DEFAULT_DEBUG_CODES: KitWithResultsSchemaType['codes'] = [
  {
    ash_code: 'D0001',
    result_codes: [
      {
        code: 'ldlCholesterol',
        prettyName: 'LDL Cholesterol',
        pretty_test_name: 'LDL Cholesterol',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0002',
    result_codes: [
      {
        code: 'vitaminD',
        prettyName: 'Vitamin D',
        pretty_test_name: 'Vitamin D',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0003',
    result_codes: [
      { code: 'hiv', prettyName: 'HIV', pretty_test_name: 'HIV', optionality_marker: 0 },
    ],
  },
  {
    ash_code: 'D0004',
    result_codes: [
      {
        code: 'chlamydiaGenital',
        prettyName: 'Chlamydia Genital',
        pretty_test_name: 'Chlamydia Genital',
        optionality_marker: 2,
      },
    ],
  },
  {
    ash_code: 'D0005',
    result_codes: [
      {
        code: 'syphilis',
        prettyName: 'Syphilis',
        pretty_test_name: 'Syphilis',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0006',
    result_codes: [
      {
        code: 'hbsAg',
        prettyName: 'Hepatitis B Surface Antigen',
        pretty_test_name: 'Hepatitis B Surface Antigen',
        optionality_marker: 1,
      },
    ],
  },
  {
    ash_code: 'D0007',
    result_codes: [
      {
        code: 'fsh',
        prettyName: 'Follicle-stimulating Hormone (FSH)',
        pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0008',
    result_codes: [
      {
        code: 'fshAbnormalityAgnostic',
        prettyName: 'Follicle-stimulating Hormone (FSH)',
        pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
        optionality_marker: 0,
      },
      {
        code: 'fshAbnormalityAgnosticCorrected',
        prettyName: 'Follicle-stimulating Hormone (FSH)',
        pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0009',
    result_codes: [
      {
        code: 'semiQuantitativeTest',
        prettyName: 'Semi Quantitative Test',
        pretty_test_name: 'Semi Quantitative Test',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0010',
    result_codes: [
      {
        code: 'semiQuantitativeTest2',
        prettyName: 'Semi Quantitative Test 2',
        pretty_test_name: 'Semi Quantitative Test 2',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0011',
    result_codes: [
      {
        code: 'uACRNumeric',
        prettyName: 'Urine Albumin-to-Creatinine Ratio',
        pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
        optionality_marker: 0,
      },
    ],
  },
  {
    ash_code: 'D0012',
    result_codes: [
      {
        code: 'uACRUnableToCalculate',
        prettyName: 'Urine Albumin-to-Creatinine Ratio',
        pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
        optionality_marker: 0,
      },
    ],
  },
]

const DEBUG_KIT_DEFAULTS = {
  outbound_tracking_id: null,
  user_id: 1,
  has_pcp: false,
  pcp: null,
  instruction_id: null,
  tests: null,
  sku: 'Debug_SKU',
  pretty_sku_name: 'Debug SKU',
} satisfies Partial<KitWithResultsSchemaType>

function makeDebugKit(
  kit: Pick<KitWithResultsSchemaType, 'kit_id' | 'kit_registration_date' | 'status' | 'events'> &
    Partial<KitWithResultsSchemaType>,
): KitWithResultsSchemaType {
  return {
    ...DEBUG_KIT_DEFAULTS,
    url: `/user/kits/results/${kit.kit_id}`,
    codes: DEFAULT_DEBUG_CODES,
    ...kit,
  }
}

const DEBUG_RESULT_DEFAULTS = {
  abnormalRangeName: '',
  correctionNumber: 0,
  inconclusiveRangeName: '',
  inconclusiveReferenceRange: null,
  normalRangeName: '',
  note: null,
  ranges: {},
  units: '',
  isAbnormal: false,
  isMultiPhysiological: false,
  isRejected: false,
} satisfies Partial<KitOrderResultLineWithCorrectionSchemaType>

function makeResult(
  r: Pick<
    KitOrderResultLineWithCorrectionSchemaType,
    'ashCode' | 'prettyName' | 'pretty_test_name' | 'testName' | 'testType' | 'resultValue'
  > &
    Partial<KitOrderResultLineWithCorrectionSchemaType>,
): KitOrderResultLineWithCorrectionSchemaType {
  return { ...DEBUG_RESULT_DEFAULTS, ...r }
}

function makeResultBase(
  r: Pick<
    KitOrderResultLineSchemaType,
    'ashCode' | 'prettyName' | 'pretty_test_name' | 'testName' | 'testType' | 'resultValue'
  > &
    Partial<KitOrderResultLineSchemaType>,
): KitOrderResultLineSchemaType {
  return { ...DEBUG_RESULT_DEFAULTS, ...r }
}

const DEBUG_PROVIDER = { firstName: 'Ada', lastName: 'Lovelace', npi: '0000000000' }

// Lets debug kits exercise the CLIA laboratory tile, which only renders when tests.labInfo is set.
const DEBUG_LAB_INFO: KitOrderLabInfoSchemaType = {
  prettyName: 'Debug Reference Laboratory',
  address: '123 Debug Lane',
  city: 'Testville',
  state: 'CA',
  zip: '90210',
  cliaId: '05D1234567',
  director: 'Grace Hopper, PHD',
  capNumber: '1234567',
}

function makeTests(
  kitId: string,
  dateReported: string,
  results: KitOrderResultLineWithCorrectionSchemaType[],
  extraPdfs?: string[],
): NonNullable<KitWithResultsSchemaType['tests']> {
  const pdf = `https://storage.googleapis.com/ash-pdf-storage-dev/debug-results/${kitId}.pdf`
  return {
    dateReported,
    kitId,
    labInfo: DEBUG_LAB_INFO,
    labName: 'debuglab',
    partnerId: 'debugpartner',
    pdf,
    pdfs: extraPdfs ? [pdf, ...extraPdfs] : [pdf],
    providerInfo: DEBUG_PROVIDER,
    results,
  }
}

function daysAfterIso(isoTimestamp: string, days: number): string {
  const date = new Date(isoTimestamp)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString()
}

// Debug kits used to exercise UI states and result edge cases.
// Toggle with localStorage.setItem('debug-kits', 'true') to append these to API responses.
export const debugKits: KitWithResultsSchemaType[] = [
  makeDebugKit({
    kit_id: 'DBG-OUT-FOR-PATIENT-DELIVERY',
    kit_registration_date: '2025-01-04T09:00:00Z',
    status: 'kit-out-for-patient-delivery',
    outbound_tracking_id: 'DBG-OUTBOUND-TRACK-006',
    events: [
      { event: 'kit-registered', timestamp: '2025-01-04T09:00:00Z' },
      { event: 'kit-in-transit-to-patient', timestamp: '2025-01-05T12:00:00Z' },
      { event: 'kit-out-for-patient-delivery', timestamp: '2025-01-06T09:00:00Z' },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-OUT-FOR-LAB-DELIVERY',
    kit_registration_date: '2025-01-04T09:00:00Z',
    status: 'kit-out-for-lab-delivery',
    inbound_tracking_id: 'DBG-INBOUND-TRACK-006',
    events: [
      { event: 'kit-registered', timestamp: '2025-01-04T09:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-05T12:00:00Z' },
      { event: 'kit-out-for-lab-delivery', timestamp: '2025-01-06T09:00:00Z' },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-OUTBOUND-TRANSIT-005',
    kit_registration_date: '2025-01-04T09:00:00Z',
    status: 'kit-in-transit-to-patient',
    outbound_tracking_id: 'DBG-OUT-TRACK-005',
    events: [
      { event: 'kit-registered', timestamp: '2025-01-04T09:00:00Z' },
      { event: 'order-received', timestamp: '2025-01-04T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2025-01-04T12:00:00Z' },
      { event: 'kit-in-transit-to-patient', timestamp: '2025-01-05T12:00:00Z' },
    ],
  }),
  // Recent registration date but older than DBG-RESULTS-READY-008; no PDF + undisplayable result triggers download banner.
  makeDebugKit({
    kit_id: 'PP687-DOWNLOAD-TRIGGER',
    user_id: 99999,
    kit_registration_date: '2026-07-07T10:00:00Z',
    instruction_id: 'adx',
    status: 'results-ready',
    sku: 'Demo_HBA1C_tray_ADX_GroundAdv',
    pretty_sku_name: 'PP-687 Download Trigger Test',
    codes: [],
    inbound_tracking_id: null,
    tests: {
      ...makeTests('PP687-DOWNLOAD-TRIGGER', '2026-07-07T09:30:00Z', [
        makeResult({
          ashCode: 'T1004',
          collectionDate: '2026-07-06T09:00:00Z',
          prettyName: 'Chlamydia Genital',
          pretty_test_name: 'Chlamydia Genital',
          referenceRange: 'POSITIVE,NEGATIVE',
          ranges: { primary: 'POSITIVE,NEGATIVE' },
          resultValue: 'negative',
          testName: 'chlamydiaGenital',
          testType: 'qualitative',
          units: null,
        }),
        makeResult({
          ashCode: 'X9999',
          collectionDate: '2026-07-06T09:00:00Z',
          prettyName: 'Unknown Panel Marker',
          pretty_test_name: 'Unknown Panel Marker',
          referenceRange: '0-5',
          resultValue: '3.1',
          testName: 'unknownPanelMarker',
          testType: 'unknown-type',
          units: null,
        }),
      ]),
      partnerId: 'demopartner',
      labName: 'ussl',
      // Intentionally absent so this debug kit still covers the "no laboratory" suppressed-tile path.
      labInfo: null,
      pdf: null,
      pdfEntities: [],
      pdfs: [],
      providerInfo: {
        firstName: 'Test',
        lastName: 'Provider',
        npi: '1234567890',
      },
    },
    events: [{ event: 'results-ready', timestamp: '2026-07-07T09:31:00Z' }],
  }),

  makeDebugKit({
    kit_id: 'DBG-REJECTED-001',
    kit_registration_date: '2025-01-01T10:00:00Z',
    status: 'kit-rejected',
    events: [
      { event: 'order-received', timestamp: '2025-01-01T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-01T10:00:00Z' },
      { event: 'kit-issue', timestamp: '2025-01-02T12:00:00Z' },
      { event: 'kit-rejected', timestamp: '2025-01-03T08:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-FOOD-ALLERGY-QUAL-009',
    kit_registration_date: '2025-01-09T10:00:00Z',
    status: 'results-ready',
    tests: makeTests('DBG-FOOD-ALLERGY-QUAL-009', '2025-01-11T12:00:00Z', [
      makeResult({
        abnormalRangeName: 'Positive',
        ashCode: 'D0100',
        collectionDate: '2025-01-10T08:00:00Z',
        normalRangeName: 'Negative',
        prettyName: 'Food Allergy Panel',
        pretty_test_name: 'Food Allergy IgE Panel',
        referenceRange: 'Negative',
        resultValue: 'positive',
        testName: 'food_allergy_panel',
        testType: 'qualitative',
        isAbnormal: true,
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2025-01-09T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2025-01-09T09:30:00Z' },
      { event: 'kit-delivered-to-patient', timestamp: '2025-01-09T15:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-09T16:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-10T07:45:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-10T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2025-01-10T14:00:00Z' },
      { event: 'results-ready', timestamp: '2025-01-11T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0100',
        result_codes: [
          {
            code: 'food_allergy_panel',
            prettyName: 'Food Allergy Panel',
            pretty_test_name: 'Food Allergy IgE Panel',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-VITD-25OH-010',
    kit_registration_date: '2025-01-10T10:00:00Z',
    status: 'results-ready',
    tests: makeTests('DBG-VITD-25OH-010', '2025-01-12T12:00:00Z', [
      makeResult({
        ashCode: 'D0101',
        collectionDate: '2025-01-11T08:00:00Z',
        normalRangeName: 'Normal',
        note: 'Sample collected properly',
        prettyName: 'Vitamin D',
        pretty_test_name: 'Vitamin D, 25-Hydroxy',
        referenceRange: '20-50',
        resultValue: '25.5',
        testName: 'vitamin_d_25_hydroxy',
        testType: 'quantitative',
        units: 'ng/mL',
      }),
      makeResult({
        abnormalRangeName: 'Low',
        ashCode: 'D0101',
        collectionDate: '2025-01-11T08:00:00Z',
        normalRangeName: 'Normal',
        note: 'Result below normal range',
        prettyName: 'Vitamin D',
        pretty_test_name: 'Vitamin D, 25-Hydroxy',
        referenceRange: '20-50',
        resultValue: '15.2',
        testName: 'vitamin_d_25_hydroxy',
        testType: 'quantitative',
        units: 'ng/mL',
        isAbnormal: true,
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2025-01-10T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-10T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-10T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-11T09:00:00Z' },
      { event: 'results-ready', timestamp: '2025-01-12T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0101',
        result_codes: [
          {
            code: 'vitamin_d_25_hydroxy',
            prettyName: 'Vitamin D',
            pretty_test_name: 'Vitamin D, 25-Hydroxy',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-KIT-ISSUE-002',
    kit_registration_date: '2025-01-02T10:00:00Z',
    status: 'kit-issue',
    events: [
      { event: 'order-received', timestamp: '2025-01-02T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-02T10:00:00Z' },
      { event: 'kit-issue', timestamp: '2025-01-03T12:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-ACCESSIONED-003',
    kit_registration_date: '2025-01-03T10:00:00Z',
    status: 'kit-accessioned',
    has_pcp: true,
    pcp: {
      id: 'pcp-1',
      user_id: '1',
      send_fax_results_to_pcp: true,
      pcp_name: 'Dr. Debug',
      pcp_fax: '555-0100',
    },
    events: [
      { event: 'order-received', timestamp: '2025-01-03T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-03T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-04T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-05T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2025-01-05T14:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-KIT-ISSUE-RESOLVED-002R',
    kit_registration_date: '2025-01-02T10:00:00Z',
    status: 'kit-issue-resolved',
    events: [
      { event: 'order-received', timestamp: '2025-01-02T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-02T10:00:00Z' },
      { event: 'kit-issue', timestamp: '2025-01-03T12:00:00Z' },
      { event: 'kit-issue-resolved', timestamp: '2025-01-03T16:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-REGISTERED-004',
    kit_registration_date: '2025-01-04T10:00:00Z',
    status: 'kit-registered',
    events: [
      { event: 'order-received', timestamp: '2025-01-04T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-04T10:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-IN-TRANSIT-005',
    kit_registration_date: '2025-01-05T10:00:00Z',
    status: 'kit-in-transit-to-lab',
    inbound_tracking_id: 'DBG-IN-TRACK-005',
    events: [
      { event: 'order-received', timestamp: '2025-01-05T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-05T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-05T12:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-SAMPLES-RECEIVED-006',
    kit_registration_date: '2025-01-06T10:00:00Z',
    status: 'test-samples-received',
    has_pcp: true,
    pcp: {
      id: 'pcp-2',
      user_id: '1',
      send_fax_results_to_pcp: false,
      pcp_name: 'Dr. Sample',
      pcp_fax: '555-0101',
    },
    events: [
      { event: 'order-received', timestamp: '2025-01-06T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-06T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-06T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-07T09:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-KIT-ISSUE-007',
    kit_registration_date: '2025-01-07T10:00:00Z',
    status: 'kit-issue',
    events: [
      { event: 'order-received', timestamp: '2025-01-07T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-07T10:00:00Z' },
      { event: 'kit-issue', timestamp: '2025-01-08T11:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-RESULTS-READY-008',
    kit_registration_date: '2026-07-08T10:00:00Z',
    status: 'results-ready',
    has_pcp: true,
    pcp: {
      id: 'pcp-3',
      user_id: '1',
      send_fax_results_to_pcp: true,
      pcp_name: 'Dr. Results',
      pcp_fax: '555-0102',
    },
    sku: 'Debug_Panel_With_A_Very_Long_Name_That_Is_Likely_To_Be_Truncated_Or_Wrap_Onto_Multiple_Lines',
    pretty_sku_name:
      'Debug Panel With A Very Long Name That Is Likely To Be Truncated Or Wrap Onto Multiple Lines',
    tests: makeTests(
      'DBG-RESULTS-READY-008',
      '2026-07-07T12:00:00Z',
      [
        // Quantitative (lower bound 0): LDL Cholesterol 0-130
        makeResult({
          ashCode: 'D0001',
          collectionDate: '2026-07-06T08:00:00Z',
          correctionNumber: 2,
          normalRangeName: 'Optimal',
          prettyName: 'LDL Cholesterol',
          pretty_test_name: 'LDL Cholesterol',
          referenceRange: '0-130',
          resultValue: '100',
          testName: 'ldlCholesterol',
          testType: 'quantitative',
          units: 'mg/dL',
          correctionHistory: [
            makeResultBase({
              abnormalRangeName: 'Elevated',
              ashCode: 'D0001',
              collectionDate: '2026-07-06T08:00:00Z',
              resultDate: daysAfterIso('2026-07-07T12:00:00Z', 2),
              normalRangeName: 'Optimal',
              prettyName: 'LDL Cholesterol',
              pretty_test_name: 'LDL Cholesterol',
              referenceRange: '0-130',
              resultValue: '179.74',
              testName: 'ldlCholesterol',
              testType: 'quantitative',
              units: 'mg/dL',
              isAbnormal: true,
            }),
            makeResultBase({
              abnormalRangeName: 'Elevated',
              ashCode: 'D0001',
              collectionDate: '2026-07-06T08:10:00Z',
              correctionNumber: 1,
              resultDate: daysAfterIso('2026-07-09T10:00:00Z', 3),
              normalRangeName: 'Optimal',
              prettyName: 'LDL Cholesterol',
              pretty_test_name: 'LDL Cholesterol',
              referenceRange: '0-130',
              resultValue: '148.3',
              testName: 'ldlCholesterol',
              testType: 'quantitative',
              units: 'mg/dL',
              isAbnormal: true,
            }),
          ],
        }),
        makeResult({
          abnormalRangeName: 'Elevated',
          ashCode: 'D0001',
          collectionDate: '2026-07-06T08:00:00Z',
          normalRangeName: 'Optimal',
          prettyName: 'LDL Cholesterol',
          pretty_test_name: 'LDL Cholesterol',
          referenceRange: '0-130',
          resultValue: '179.74',
          testName: 'ldlCholesterol',
          testType: 'quantitative',
          units: 'mg/dL',
          isAbnormal: true,
        }),
        makeResult({
          ashCode: 'D0001',
          collectionDate: '2026-07-06T08:00:00Z',
          normalRangeName: 'Optimal',
          prettyName: 'LDL Cholesterol',
          pretty_test_name: 'LDL Cholesterol',
          referenceRange: '0-130',
          resultValue: 'quantity-not-sufficient',
          testName: 'ldlCholesterol',
          testType: 'quantitative',
          units: 'mg/dL',
        }),

        // Quantitative (lower bound above 0): Vitamin D 25-100
        makeResult({
          abnormalRangeName: 'Low',
          ashCode: 'D0002',
          collectionDate: '2026-07-06T08:05:00Z',
          normalRangeName: 'Normal',
          prettyName: 'Vitamin D',
          pretty_test_name: 'Vitamin D',
          referenceRange: '25-100',
          resultValue: '10',
          testName: 'vitaminD',
          testType: 'quantitative',
          units: 'ng/mL',
          isAbnormal: true,
        }),
        makeResult({
          ashCode: 'D0002',
          collectionDate: '2026-07-06T08:05:00Z',
          correctionNumber: 1,
          normalRangeName: 'Normal',
          prettyName: 'Vitamin D',
          pretty_test_name: 'Vitamin D',
          referenceRange: '25-100',
          resultValue: '60',
          testName: 'vitaminD',
          testType: 'quantitative',
          units: 'ng/mL',
          correctionHistory: [
            makeResultBase({
              abnormalRangeName: 'Low',
              ashCode: 'D0002',
              collectionDate: '2026-07-06T08:05:00Z',
              resultDate: daysAfterIso('2026-07-07T12:00:00Z', 4),
              normalRangeName: 'Normal',
              prettyName: 'Vitamin D',
              pretty_test_name: 'Vitamin D',
              referenceRange: '25-100',
              resultValue: '10',
              testName: 'vitaminD',
              testType: 'quantitative',
              units: 'ng/mL',
              isAbnormal: true,
            }),
          ],
        }),
        makeResult({
          abnormalRangeName: 'Elevated',
          ashCode: 'D0002',
          collectionDate: '2026-07-06T08:05:00Z',
          normalRangeName: 'Normal',
          prettyName: 'Vitamin D',
          pretty_test_name: 'Vitamin D',
          referenceRange: '25-100',
          resultValue: '150',
          testName: 'vitaminD',
          testType: 'quantitative',
          units: 'ng/mL',
          isAbnormal: true,
        }),
        makeResult({
          ashCode: 'D0002',
          collectionDate: '2026-07-06T08:05:00Z',
          normalRangeName: 'Normal',
          prettyName: 'Vitamin D',
          pretty_test_name: 'Vitamin D',
          referenceRange: '25-100',
          resultValue: 'quantity-not-sufficient',
          testName: 'vitaminD',
          testType: 'quantitative',
          units: 'ng/mL',
        }),

        // Qualitative: not-detected / detected / reactive / test-not-performed
        makeResult({
          ashCode: 'D0003',
          collectionDate: '2026-07-06T08:10:00Z',
          correctionNumber: 1,
          prettyName: 'HIV',
          pretty_test_name: 'HIV',
          referenceRange: 'NOT-DETECTED,DETECTED',
          resultValue: 'not-detected',
          testName: 'hiv',
          testType: 'qualitative',
          correctionHistory: [
            makeResultBase({
              abnormalRangeName: 'Detected',
              ashCode: 'D0003',
              collectionDate: '2026-07-06T08:10:00Z',
              resultDate: daysAfterIso('2026-07-07T12:00:00Z', 1),
              prettyName: 'HIV',
              pretty_test_name: 'HIV',
              referenceRange: 'NOT-DETECTED,DETECTED',
              resultValue: 'detected',
              testName: 'hiv',
              testType: 'qualitative',
              isAbnormal: true,
            }),
          ],
        }),
        makeResult({
          abnormalRangeName: 'Detected',
          ashCode: 'D0004',
          collectionDate: '2026-07-06T08:10:00Z',
          prettyName: 'Chlamydia Genital',
          pretty_test_name: 'Chlamydia Genital',
          referenceRange: 'NOT-DETECTED,DETECTED',
          resultValue: 'detected',
          testName: 'chlamydiaGenital',
          testType: 'qualitative',
          isAbnormal: true,
        }),
        makeResult({
          abnormalRangeName: 'Reactive',
          ashCode: 'D0005',
          collectionDate: '2026-07-06T08:10:00Z',
          correctionNumber: 1,
          normalRangeName: 'Non-reactive',
          prettyName: 'Syphilis',
          pretty_test_name: 'Syphilis',
          referenceRange: 'NON-REACTIVE,REACTIVE',
          resultValue: 'reactive',
          testName: 'syphilis',
          testType: 'qualitative',
          isAbnormal: true,
          correctionHistory: [
            makeResultBase({
              ashCode: 'D0005',
              collectionDate: '2026-07-06T08:10:00Z',
              resultDate: daysAfterIso('2026-07-07T15:00:00Z', 5),
              normalRangeName: 'Non-reactive',
              prettyName: 'Syphilis',
              pretty_test_name: 'Syphilis',
              referenceRange: 'NON-REACTIVE,REACTIVE',
              resultValue: 'non-reactive',
              testName: 'syphilis',
              testType: 'qualitative',
            }),
          ],
        }),
        makeResult({
          ashCode: 'D0006',
          collectionDate: '2026-07-06T08:10:00Z',
          prettyName: 'Hepatitis B Surface Antigen',
          pretty_test_name: 'Hepatitis B Surface Antigen',
          referenceRange: 'NON-REACTIVE,REACTIVE',
          resultValue: 'test-not-performed',
          testName: 'hbsAg',
          testType: 'qualitative',
        }),

        // Variable-range quantitative: FSH (multi-population → QuantitativeVariableRangeCard)
        makeResult({
          ashCode: 'D0007',
          collectionDate: '2026-07-06T08:15:00Z',
          prettyName: 'Follicle-stimulating Hormone (FSH)',
          pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
          referenceRange: null,
          resultValue: '6.8',
          testName: 'fsh',
          testType: 'quantitative',
          units: 'mIU/mL',
          isMultiPhysiological: true,
          isRejected: false,
          ranges: {
            male: { gte: 1.5, lt: 12.4 },
            follicular_phase: { gte: 3.5, lt: 12.5 },
            ovulatory_phase: { gte: 4.7, lt: 21.5 },
            luteal_phase: { gte: 1.7, lt: 7.7 },
          },
        }),
        makeResult({
          ashCode: 'D0007',
          collectionDate: '2026-07-06T08:15:00Z',
          correctionNumber: 1,
          prettyName: 'Follicle-stimulating Hormone (FSH)',
          pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
          referenceRange: null,
          resultValue: '6.8',
          testName: 'fsh',
          testType: 'quantitative',
          units: 'mIU/mL',
          isMultiPhysiological: true,
          isRejected: false,
          ranges: {
            male: { gte: 1.5, lt: 12.4 },
            follicular_phase: { gte: 3.5, lt: 12.5 },
            ovulatory_phase: { gte: 4.7, lt: 21.5 },
            luteal_phase: { gte: 1.7, lt: 7.7 },
          },
          correctionHistory: [
            makeResultBase({
              ashCode: 'D0007',
              collectionDate: '2026-07-06T08:15:00Z',
              resultDate: daysAfterIso('2026-07-08T08:00:00Z', 2),
              prettyName: 'Follicle-stimulating Hormone (FSH)',
              pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
              referenceRange: null,
              resultValue: '18.3',
              testName: 'fsh',
              testType: 'quantitative',
              units: 'mIU/mL',
              isMultiPhysiological: true,
              isRejected: false,
              ranges: {
                male: { gte: 1.5, lt: 12.4 },
                follicular_phase: { gte: 3.5, lt: 12.5 },
                ovulatory_phase: { gte: 4.7, lt: 21.5 },
                luteal_phase: { gte: 1.7, lt: 7.7 },
              },
            }),
          ],
        }),

        // Abnormality-agnostic quantitative: FSH with N/A reference range → QuantitativeResultAbnormalityAgnosticCard
        makeResult({
          ashCode: 'D0008',
          collectionDate: '2026-07-06T08:20:00Z',
          prettyName: 'Follicle-stimulating Hormone (FSH)',
          pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
          referenceRange: 'N/A',
          resultValue: '6.8',
          testName: 'fshAbnormalityAgnostic',
          testType: 'quantitative',
          units: 'mIU/mL',
          isAbnormal: null,
        }),
        makeResult({
          ashCode: 'D0008',
          collectionDate: '2026-07-06T08:20:00Z',
          correctionNumber: 1,
          prettyName: 'Follicle-stimulating Hormone (FSH)',
          pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
          referenceRange: 'N/A',
          resultValue: '6.8',
          testName: 'fshAbnormalityAgnosticCorrected',
          testType: 'quantitative',
          units: 'mIU/mL',
          isAbnormal: null,
          correctionHistory: [
            makeResultBase({
              ashCode: 'D0008',
              collectionDate: '2026-07-06T08:20:00Z',
              resultDate: daysAfterIso('2026-07-08T09:00:00Z', 3),
              prettyName: 'Follicle-stimulating Hormone (FSH)',
              pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
              referenceRange: 'N/A',
              resultValue: '18.3',
              testName: 'fshAbnormalityAgnosticCorrected',
              testType: 'quantitative',
              units: 'mIU/mL',
              isAbnormal: null,
            }),
          ],
        }),

        // Semi-quantitative: numeric result value
        makeResult({
          ashCode: 'D0009',
          collectionDate: '2026-07-06T08:25:00Z',
          normalRangeName: 'Normal',
          prettyName: 'Semi Quantitative Test',
          pretty_test_name: 'Semi Quantitative Test',
          referenceRange: '10-20',
          resultValue: '10',
          testName: 'semiQuantitativeTest',
          testType: 'semi-quantitative',
          units: 'md/gl',
          ranges: { primary: { gte: 10, lt: 20 } },
        }),

        // Semi-quantitative: qualitative result value
        makeResult({
          ashCode: 'D0010',
          collectionDate: '2026-07-06T08:25:00Z',
          prettyName: 'Semi Quantitative Test 2',
          pretty_test_name: 'Semi Quantitative Test 2',
          referenceRange: '10-20',
          resultValue: 'negative',
          testName: 'semiQuantitativeTest2',
          testType: 'semi-quantitative',
          units: null,
          ranges: { primary: { gte: 10, lt: 20 } },
        }),

        // Semi-quantitative uACR: numeric result value
        makeResult({
          ashCode: 'D0011',
          collectionDate: '2026-07-06T08:30:00Z',
          prettyName: 'Urine Albumin-to-Creatinine Ratio',
          pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
          referenceRange: '0-30',
          resultValue: '12',
          testName: 'uACRNumeric',
          testType: 'semi-quantitative',
          units: 'mg/g',
          isAbnormal: false,
          ranges: {
            Low: { gte: 0, lt: 0 },
            Elevated: { gte: 30, lt: null },
            primary: { gte: 0, lt: 30 },
          },
        }),

        // Semi-quantitative uACR: unable-to-calculate
        makeResult({
          ashCode: 'D0012',
          collectionDate: '2026-07-06T08:30:00Z',
          prettyName: 'Urine Albumin-to-Creatinine Ratio',
          pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
          referenceRange: null,
          resultValue: 'unable-to-calculate',
          testName: 'albuminCreatinineRatio',
          testType: 'semi-quantitative',
          units: null,
          isAbnormal: true,
          ranges: {},
        }),
      ],
      [
        'https://storage.googleapis.com/ash-pdf-storage-dev/debug-results/DBG-RESULTS-READY-008-v2.pdf',
      ],
    ),
    events: [
      { event: 'order-received', timestamp: '2026-07-05T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2026-07-05T09:30:00Z' },
      { event: 'kit-delivered-to-patient', timestamp: '2026-07-05T15:00:00Z' },
      { event: 'kit-registered', timestamp: '2026-07-05T16:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2026-07-06T07:45:00Z' },
      { event: 'test-samples-received', timestamp: '2026-07-06T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2026-07-06T14:00:00Z' },
      { event: 'results-ready', timestamp: '2026-07-07T12:00:00Z' },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-CORRECTION-HISTORY-011',
    kit_registration_date: '2025-01-11T10:00:00Z',
    status: 'results-ready',
    tests: makeTests('DBG-CORRECTION-HISTORY-011', '2025-01-13T12:00:00Z', [
      makeResult({
        ashCode: 'D0110',
        collectionDate: '2025-01-12T08:00:00Z',
        correctionNumber: 2,
        normalRangeName: 'Optimal',
        prettyName: 'LDL Cholesterol',
        pretty_test_name: 'LDL Cholesterol',
        referenceRange: '0-130',
        resultValue: '110',
        testName: 'ldlCholesterol',
        testType: 'quantitative',
        units: 'mg/dL',
        correctionHistory: [
          makeResultBase({
            abnormalRangeName: 'Elevated',
            ashCode: 'D0110',
            collectionDate: '2025-01-12T08:00:00Z',
            correctionNumber: 1,
            resultDate: daysAfterIso('2025-01-15T10:00:00Z', 2),
            normalRangeName: 'Optimal',
            prettyName: 'LDL Cholesterol',
            pretty_test_name: 'LDL Cholesterol',
            referenceRange: '0-130',
            resultValue: '155',
            testName: 'ldlCholesterol',
            testType: 'quantitative',
            units: 'mg/dL',
            isAbnormal: true,
          }),
          makeResultBase({
            abnormalRangeName: 'Elevated',
            ashCode: 'D0110',
            collectionDate: '2025-01-12T07:45:00Z',
            correctionNumber: 0,
            resultDate: daysAfterIso('2025-01-13T12:00:00Z', 1),
            normalRangeName: 'Optimal',
            prettyName: 'LDL Cholesterol',
            pretty_test_name: 'LDL Cholesterol',
            referenceRange: '0-130',
            resultValue: '172',
            testName: 'ldlCholesterol',
            testType: 'quantitative',
            units: 'mg/dL',
            isAbnormal: true,
          }),
        ],
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2025-01-11T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-11T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-11T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-12T09:00:00Z' },
      { event: 'results-ready', timestamp: '2025-01-13T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0110',
        result_codes: [
          {
            code: 'ldlCholesterol',
            prettyName: 'LDL Cholesterol',
            pretty_test_name: 'LDL Cholesterol',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-VARIABLE-RANGE-ONLY-012',
    kit_registration_date: '2025-01-12T10:00:00Z',
    status: 'results-ready',
    sku: 'Debug_Variable_Range_Only',
    pretty_sku_name: 'Debug — Variable Range Only',
    tests: makeTests('DBG-VARIABLE-RANGE-ONLY-012', '2025-01-14T12:00:00Z', [
      makeResult({
        ashCode: 'D0007',
        collectionDate: '2025-01-13T08:15:00Z',
        prettyName: 'Follicle-stimulating Hormone (FSH)',
        pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
        referenceRange: null,
        resultValue: '6.8',
        testName: 'fsh',
        testType: 'quantitative',
        units: 'mIU/mL',
        isAbnormal: false,
        isMultiPhysiological: true,
        isRejected: false,
        ranges: {
          male: { gte: 1.5, lt: 12.4 },
          follicular_phase: { gte: 3.5, lt: 12.5 },
          ovulatory_phase: { gte: 4.7, lt: 21.5 },
          luteal_phase: { gte: 1.7, lt: 7.7 },
        },
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2025-01-12T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-12T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-12T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-13T09:00:00Z' },
      { event: 'results-ready', timestamp: '2025-01-14T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0007',
        result_codes: [
          {
            code: 'fsh',
            prettyName: 'Follicle-stimulating Hormone (FSH)',
            pretty_test_name: 'Follicle-stimulating Hormone (FSH)',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),

  makeDebugKit({
    kit_id: 'DBG-COMPARATOR-PROMOTION-013',
    kit_registration_date: '2025-01-13T10:00:00Z',
    status: 'results-ready',
    sku: 'Debug_Comparator_Promotion',
    pretty_sku_name: 'Debug — Comparator Promotion',
    tests: makeTests('DBG-COMPARATOR-PROMOTION-013', '2025-01-15T12:00:00Z', [
      // Reported case 1: "<0.08" vs "0-0.82", not abnormal → promoted, Normal.
      makeResult({
        ashCode: 'D0014',
        collectionDate: '2025-01-14T08:00:00Z',
        normalRangeName: 'Normal',
        prettyName: 'AMH',
        pretty_test_name: 'Anti-Müllerian Hormone (AMH)',
        referenceRange: '0-0.82',
        resultValue: '<0.08',
        testName: 'amhNormal',
        testType: 'quantitative',
        units: 'ng/mL',
        isAbnormal: false,
      }),
      // Reported case 2: "<0.08" vs "0.18-5.68", abnormal → promoted, Low.
      makeResult({
        abnormalRangeName: 'Low',
        ashCode: 'D0015',
        collectionDate: '2025-01-14T08:05:00Z',
        normalRangeName: 'Normal',
        prettyName: 'AMH',
        pretty_test_name: 'Anti-Müllerian Hormone (AMH)',
        referenceRange: '0.18-5.68',
        resultValue: '<0.08',
        testName: 'amhLow',
        testType: 'quantitative',
        units: 'ng/mL',
        isAbnormal: true,
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2025-01-13T09:00:00Z' },
      { event: 'kit-registered', timestamp: '2025-01-13T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2025-01-13T12:00:00Z' },
      { event: 'test-samples-received', timestamp: '2025-01-14T09:00:00Z' },
      { event: 'results-ready', timestamp: '2025-01-15T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0014',
        result_codes: [
          {
            code: 'amhNormal',
            prettyName: 'AMH',
            pretty_test_name: 'Anti-Müllerian Hormone (AMH)',
            optionality_marker: 0,
          },
        ],
      },
      {
        ash_code: 'D0015',
        result_codes: [
          {
            code: 'amhLow',
            prettyName: 'AMH',
            pretty_test_name: 'Anti-Müllerian Hormone (AMH)',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-PROVIDER-APPROVAL-013',
    kit_registration_date: '2026-07-06T10:00:00Z',
    status: 'results-ready-provider-approval-completed',
    tests: makeTests('DBG-PROVIDER-APPROVAL-013', '2026-07-05T12:00:00Z', [
      makeResult({
        ashCode: 'D0102',
        collectionDate: '2026-07-04T08:00:00Z',
        normalRangeName: 'Optimal',
        prettyName: 'LDL Cholesterol',
        pretty_test_name: 'LDL Cholesterol',
        referenceRange: '0-130',
        resultValue: '95',
        testName: 'ldlCholesterol',
        testType: 'quantitative',
        units: 'mg/dL',
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2026-07-01T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2026-07-01T09:30:00Z' },
      { event: 'kit-delivered-to-patient', timestamp: '2026-07-01T15:00:00Z' },
      { event: 'kit-registered', timestamp: '2026-07-02T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2026-07-03T07:45:00Z' },
      { event: 'test-samples-received', timestamp: '2026-07-03T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2026-07-04T14:00:00Z' },
      { event: 'results-ready', timestamp: '2026-07-05T12:00:00Z' },
      { event: 'provider-approval-completed', timestamp: '2026-07-06T09:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0102',
        result_codes: [
          {
            code: 'ldlCholesterol',
            prettyName: 'LDL Cholesterol',
            pretty_test_name: 'LDL Cholesterol',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-UACR-UTC-REJECTED-014',
    kit_registration_date: '2026-08-20T10:00:00Z',
    status: 'results-ready',
    tests: makeTests('DBG-UACR-UTC-REJECTED-014', '2026-08-21T12:00:00Z', [
      makeResult({
        ashCode: 'D0012',
        collectionDate: '2026-08-20T08:30:00Z',
        prettyName: 'Urine Albumin-to-Creatinine Ratio',
        pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
        referenceRange: null,
        resultValue: 'unable-to-calculate',
        testName: 'albuminCreatinineRatio',
        testType: 'semi-quantitative',
        units: null,
        isAbnormal: true,
        isRejected: true,
        ranges: {},
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2026-08-18T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2026-08-18T09:30:00Z' },
      { event: 'kit-delivered-to-patient', timestamp: '2026-08-18T15:00:00Z' },
      { event: 'kit-registered', timestamp: '2026-08-19T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2026-08-20T07:45:00Z' },
      { event: 'test-samples-received', timestamp: '2026-08-20T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2026-08-20T14:00:00Z' },
      { event: 'results-ready', timestamp: '2026-08-21T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0012',
        result_codes: [
          {
            code: 'uACRUnableToCalculate',
            prettyName: 'Urine Albumin-to-Creatinine Ratio',
            pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),
  makeDebugKit({
    kit_id: 'DBG-UACR-UTC-ABNORMAL-015',
    kit_registration_date: '2026-08-20T10:00:00Z',
    status: 'results-ready',
    tests: makeTests('DBG-UACR-UTC-ABNORMAL-015', '2026-08-21T12:00:00Z', [
      makeResult({
        ashCode: 'D0012',
        collectionDate: '2026-08-20T08:30:00Z',
        prettyName: 'Urine Albumin-to-Creatinine Ratio',
        pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
        referenceRange: null,
        resultValue: 'unable-to-calculate',
        testName: 'albuminCreatinineRatio',
        testType: 'semi-quantitative',
        units: null,
        isAbnormal: true,
        ranges: {},
      }),
    ]),
    events: [
      { event: 'order-received', timestamp: '2026-08-18T09:00:00Z' },
      { event: 'order-fulfilled', timestamp: '2026-08-18T09:30:00Z' },
      { event: 'kit-delivered-to-patient', timestamp: '2026-08-18T15:00:00Z' },
      { event: 'kit-registered', timestamp: '2026-08-19T10:00:00Z' },
      { event: 'kit-in-transit-to-lab', timestamp: '2026-08-20T07:45:00Z' },
      { event: 'test-samples-received', timestamp: '2026-08-20T12:00:00Z' },
      { event: 'kit-accessioned', timestamp: '2026-08-20T14:00:00Z' },
      { event: 'results-ready', timestamp: '2026-08-21T12:00:00Z' },
    ],
    codes: [
      {
        ash_code: 'D0012',
        result_codes: [
          {
            code: 'uACRUnableToCalculate',
            prettyName: 'Urine Albumin-to-Creatinine Ratio',
            pretty_test_name: 'Urine Albumin-to-Creatinine Ratio',
            optionality_marker: 0,
          },
        ],
      },
    ],
  }),
]

export default debugKits
